import React from "react";
import { useAppSelector, useMediaQuery } from "../../../hooks";
import { selectDarkmode } from "../../../states/slices/globalReducer";
import { FaFilePdf } from "react-icons/fa";
import { formatDate } from "../../../utils-func/functions";
import { AI_PHOTO } from "../../../utils-func/image_exports";

function SharedChats({ chats }: any) {
  const darkmode = useAppSelector(selectDarkmode);
  const isMobile = useMediaQuery("(max-width: 640px)");
  const semanticColors = (isDarkMode: boolean) => ({
    title: isDarkMode
      ? "text-blue-400 font-semibold text-xl"
      : "text-blue-700 font-semibold text-xl",
    subtitle: isDarkMode
      ? "text-purple-400 font-medium text-lg"
      : "text-purple-700 font-medium text-lg",
    caseTitle: isDarkMode
      ? "text-blue-400 font-semibold"
      : "text-blue-700 font-semibold",
    legalPrinciple: isDarkMode ? "text-emerald-400" : "text-emerald-700",
    citation: isDarkMode ? "text-purple-400 italic" : "text-purple-700 italic",
    conclusion: isDarkMode
      ? "text-rose-400 font-medium"
      : "text-rose-700 font-medium",
    warning: isDarkMode ? "text-amber-400" : "text-amber-700",
    emphasis: isDarkMode ? "text-indigo-400" : "text-indigo-700",
    procedural: isDarkMode ? "text-cyan-400" : "text-cyan-700",
    evidence: isDarkMode ? "text-teal-400" : "text-teal-700",
    statute: isDarkMode ? "text-violet-400" : "text-violet-700",
    default: isDarkMode ? "text-gray-100" : "text-gray-900",
  });
  const colors = semanticColors(darkmode);

  const legalPatterns = [
    {
      regex: /^#\s+(.+?)$/gm,
      className: colors.title,
      removeMarker: true,
    },
    {
      regex: /^#{2,}\s+(.+?)$/gm,
      className: colors.subtitle,
      removeMarker: true,
    },
    {
      regex:
        /(?:^|\n)(?:CASE:|IN THE MATTER OF:|SUIT NO:|APPEAL NO:)(.+?)(?:\n|$)/gi,
      className: colors.caseTitle,
    },
    {
      regex:
        /(?:^|\n)(?:PRINCIPLE:|LEGAL PRINCIPLE:|RATIO:|RATIO DECIDENDI:)(.+?)(?:\n|$)/gi,
      className: colors.legalPrinciple,
    },
    {
      regex: /\[([^\]]+)\]|\(([^\)]+)\)/g,
      className: colors.citation,
    },
    {
      regex:
        /(?:^|\n)(?:CONCLUSION:|JUDGMENT:|HOLDING:|ORDER:|DECISION:)(.+?)(?:\n|$)/gi,
      className: colors.conclusion,
    },
    {
      regex:
        /(?:^|\n)(?:WARNING:|IMPORTANT:|NOTE:|CAVEAT:|DISCLAIMER:)(.+?)(?:\n|$)/gi,
      className: colors.warning,
    },
    {
      regex: /(?:^|\n)(?:PROCEDURE:|PROCEEDINGS:|TIMELINE:)(.+?)(?:\n|$)/gi,
      className: colors.procedural,
    },
    {
      regex: /(?:^|\n)(?:EVIDENCE:|EXHIBITS:|PROOF:)(.+?)(?:\n|$)/gi,
      className: colors.evidence,
    },
    {
      regex: /(?:^|\n)(?:STATUTE:|SECTION:|LAW:|ACT:)(.+?)(?:\n|$)/gi,
      className: colors.statute,
    },
    {
      regex: /\*\*(.+?)\*\*/g,
      className: colors.emphasis,
    },
  ];

  const colorizeText = (text: string) => {
    if (!text) return null;

    const lines = text.split(/\n/);
    const processedLines = lines.map((line, index) => {
      if (!line.trim()) {
        return <div key={index} className="h-4" />;
      }

      let processedLine = line;
      let hasMatch = false;
      let className = colors.default;

      for (const {
        regex,
        className: patternClass,
        removeMarker,
      } of legalPatterns) {
        const matches = Array.from(line.matchAll(new RegExp(regex)));
        if (matches.length > 0) {
          hasMatch = true;
          matches.forEach((match) => {
            const content = match[1] || match[0];
            // Remove the markdown markers if removeMarker is true
            processedLine = removeMarker
              ? content
              : processedLine.replace(
                  match[0],
                  `<span class="${patternClass}">${content}</span>`
                );
          });
          className = patternClass;
        }
      }

      // Handle markdown-style formatting within colored text
      processedLine = processedLine
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        .replace(/\*(.*?)\*/g, "<em>$1</em>")
        .replace(/`(.*?)`/g, "<code>$1</code>")
        .replace(/~~(.*?)~~/g, "<del>$1</del>");

      return (
        <div
          key={index}
          className={`${hasMatch ? "" : className} leading-relaxed py-1`}
          dangerouslySetInnerHTML={{ __html: processedLine }}
        />
      );
    });

    return <div className="space-y-1">{processedLines}</div>;
  };

  return (
    <div>
      {chats?.length > 0 && (
        <>
          <div className="flex items-center justify-center flex-col">
            <span
              className={`text-[13px] font-medium  text-center ${
                darkmode ? "text-gray-300" : "text-[#3F454D]"
              }`}
            >
              This chat was shared from another user
            </span>
          </div>
          {chats?.map((mess: any) => {
            return (
              <div className="flex flex-col gap-y-10 mt-8" key={mess?.id}>
                {mess?.sender === "user" ? (
                  <div className="w-full flex flex-col items-end justify-end gap-y-2">
                    <div className="flex items-end flex-col gap-y-2">
                      {/* Display attached files */}
                      {mess?.file_path?.length > 0 &&
                        mess?.file_path.map((file: any) => {
                          const isImage = /\.(jpg|jpeg|png|gif)$/i.test(file);
                          return isImage ? (
                            <div className="lg:w-[200px] w-[75px] h-[75px] rounded-lg lg:h-[200px] -mb-7">
                              <img
                                src={file}
                                className="w-full h-full rounded-2xl object-contain"
                                alt="Uploaded File"
                              />
                            </div>
                          ) : (
                            <FaFilePdf size={isMobile ? 75 : 150} color="red" />
                          );
                        })}
                      {/* User message bubble */}
                      <div
                        className={`px-4 py-3 ${
                          darkmode
                            ? "border-0 bg-[#232627]"
                            : "border border-[#E8ECEF] bg-white"
                        } rounded-2xl lg:max-w-md md:max-w-lg sm:max-w-sm max-w-full`}
                      >
                        <span
                          className={`lg:text-base md:text-sm text-xs font-semibold ${
                            darkmode ? "text-white/80" : "text-[#6E6E6E]"
                          } break-words`}
                        >
                          {mess?.content}
                        </span>
                      </div>
                      <span className="lg:text-sm text-xs font-normal text-[#C9C9C9]">
                        {formatDate(mess?.created_at)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="w-full flex gap-y-2 items-start gap-x-2">
                    <img
                      src={AI_PHOTO}
                      className="lg:w-[30px] lg:h-[30px] w-[25px] h-[25px]"
                      alt="AI"
                    />
                    <div className="flex flex-col items-start gap-y-1 -mt-3">
                      <div className="px-4 py-2 rounded-xl lg:max-w-full md:max-w-lg sm:max-w-sm max-w-full">
                        <div className="lg:text-base md:text-sm text-xs font-normal overflow-hidden text-[#6E6E6E] break-words">
                          {colorizeText(mess?.content)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <div className="flex items-center justify-center flex-col my-6">
            <span
              className={`text-[13px] font-medium  text-center ${
                darkmode ? "text-gray-300" : "text-[#3F454D]"
              }`}
            >
              New chats continues here
            </span>
          </div>
        </>
      )}
    </div>
  );
}

export default SharedChats;
